import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const values = [
  {
    icon: 'ri-heart-3-line',
    title: 'Passion First',
    description: 'We don\'t just print — we obsess over every detail. This started as a hobby and grew into a mission to make 3D printing accessible to everyone.',
    gradient: 'from-fuchsia-500 to-pink-500',
  },
  {
    icon: 'ri-focus-3-line',
    title: 'Precision Always',
    description: 'Every print is dialed in with care. We calibrate, test, and refine until the result matches your vision — no shortcuts, no compromises.',
    gradient: 'from-orange-400 to-yellow-400',
  },
  {
    icon: 'ri-lightbulb-line',
    title: 'Creative Problem Solving',
    description: 'Got a weird idea? Good. We love a challenge. Our background in design and engineering means we can tackle almost anything you throw at us.',
    gradient: 'from-cyan-400 to-teal-500',
  },
  {
    icon: 'ri-user-heart-line',
    title: 'Client-Centered',
    description: 'You\'re not just an order number. We communicate every step of the way and won\'t rest until you\'re genuinely thrilled with the result.',
    gradient: 'from-violet-500 to-fuchsia-500',
  },
];

const milestones = [
  { year: '2020', event: 'Bought our first 3D printer as a birthday gift — immediately got obsessed.' },
  { year: '2021', event: 'Started printing for friends and family. Word spread fast.' },
  { year: '2022', event: 'Expanded to 4 printers and launched Reader Brothers Printing officially.' },
  { year: '2023', event: 'Hit 100+ completed projects. Added multi-color and resin printing capabilities.' },
  { year: '2024', event: 'Partnered with local businesses for prototyping and product development.' },
  { year: '2025', event: 'Now serving clients nationwide with 200+ projects and counting.' },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-6 md:px-10 overflow-hidden bg-[#0f0f14]">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/79c54b7e49ea189c82409e0000c33ba0.jpg"
            alt="Reader Brothers Workshop"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f0f14]/60 to-[#0f0f14]"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span className="text-xs uppercase tracking-[3px] font-bold bg-gradient-to-r from-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
            Our Story
          </span>
          <h1 className="text-5xl md:text-6xl font-black text-white mt-4 leading-tight">
            Two Brothers,<br />
            <span className="bg-gradient-to-r from-fuchsia-400 via-orange-300 to-cyan-400 bg-clip-text text-transparent">
              One Passion
            </span>
          </h1>
          <p className="text-white/60 text-lg mt-6 leading-relaxed max-w-2xl mx-auto">
            We&apos;re not a factory. We&apos;re two brothers who genuinely love what we do — and it shows in every single print we deliver.
          </p>
        </div>
      </section>

      {/* Meet the Brothers */}
      <section className="py-24 px-6 md:px-10 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            {/* Image */}
            <div className="flex-1 relative">
              <div className="relative rounded-3xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=two%20young%20brothers%20smiling%20in%20a%20creative%20workshop%2C%20surrounded%20by%20colorful%203D%20printed%20objects%20on%20shelves%2C%20casual%20friendly%20atmosphere%2C%20warm%20studio%20lighting%2C%20genuine%20happy%20expressions%2C%20professional%20lifestyle%20photography&width=700&height=600&seq=brothers-001&orientation=landscape"
                  alt="The Reader Brothers"
                  className="w-full h-[420px] object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -right-4 bg-white rounded-2xl px-6 py-4 border border-gray-100">
                <p className="text-2xl font-black bg-gradient-to-r from-fuchsia-500 to-orange-400 bg-clip-text text-transparent">200+</p>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Happy Clients</p>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1">
              <span className="text-xs uppercase tracking-[3px] font-bold bg-gradient-to-r from-fuchsia-500 to-orange-400 bg-clip-text text-transparent">
                Meet The Team
              </span>
              <h2 className="text-4xl font-black text-gray-900 mt-3 mb-6 leading-tight">
                We Are Reader Brothers Printing
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-5">
                It started with a single 3D printer and a shared obsession. We&apos;re two brothers who grew up taking things apart, building things, and dreaming up ideas that didn&apos;t exist yet. When we discovered 3D printing, it felt like a superpower — and we haven&apos;t stopped since.
              </p>
              <p className="text-gray-600 text-base leading-relaxed mb-5">
                What began as a hobby quickly turned into something bigger. Friends started asking for prints. Then friends of friends. Then businesses. Now we&apos;re helping clients across the country turn their ideas into physical reality — from engineering prototypes to one-of-a-kind art pieces.
              </p>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                We bring different skills to the table — one of us is deep into design and aesthetics, the other is obsessed with engineering and precision. Together, we cover everything from concept to finished product.
              </p>
              <Link
                to="/contact"
                className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-white text-base bg-gradient-to-r from-fuchsia-500 via-orange-400 to-cyan-400 hover:opacity-90 transition-opacity"
              >
                Work With Us
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 px-6 md:px-10 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[3px] font-bold bg-gradient-to-r from-cyan-500 to-fuchsia-500 bg-clip-text text-transparent">
              What Drives Us
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mt-3">Our Values</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div key={i} className="bg-white rounded-2xl p-6" style={{ border: '1px solid #f0f0f0' }}>
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${v.gradient} mb-5`}>
                  <i className={`${v.icon} text-white text-xl`}></i>
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-3">{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 px-6 md:px-10 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[3px] font-bold bg-gradient-to-r from-orange-400 to-fuchsia-500 bg-clip-text text-transparent">
              How We Got Here
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mt-3">Our Journey</h2>
          </div>
          <div className="relative">
            {/* Line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-fuchsia-400 via-orange-400 to-cyan-400 hidden sm:block"></div>
            <div className="space-y-10">
              {milestones.map((m, i) => (
                <div key={i} className="flex gap-8 items-start">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-fuchsia-500 to-orange-400 text-white font-black text-xs z-10">
                    {m.year.slice(2)}
                  </div>
                  <div className="flex-1 pb-2">
                    <p className="text-xs font-bold text-fuchsia-500 uppercase tracking-widest mb-1">{m.year}</p>
                    <p className="text-gray-700 text-base leading-relaxed">{m.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 px-6 md:px-10 bg-gradient-to-r from-fuchsia-600 via-orange-500 to-cyan-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">Ready to Build Something?</h2>
          <p className="text-white/80 text-lg mb-8">
            Drop us a message and let&apos;s talk about your project. We&apos;d love to hear your idea.
          </p>
          <Link
            to="/contact"
            className="whitespace-nowrap inline-flex items-center gap-2 px-10 py-4 rounded-full font-bold text-fuchsia-600 text-base bg-white hover:bg-white/90 transition-opacity"
          >
            Get in Touch
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
